import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getAssessment, submitAssessment, requestFullResults } from "@/lib/api";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const GREEN = "#1F3D2E";

export default function Assessment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("intro");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [safetyFlags, setSafetyFlags] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setStage("intro"); setStep(0); setAnswers({}); setSafetyFlags({});
    getAssessment(id).then(setData).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <Section className="pt-24 pb-24 text-center">
        <h1 className="font-playfair text-3xl text-stone-900">Assessment not found</h1>
        <Link to="/assessments" className="text-[#1F3D2E] font-semibold mt-4 inline-block" data-testid="back-to-hub">Back to all assessments</Link>
      </Section>
    );
  }
  if (!data) return <Section className="pt-24 pb-24"><p className="text-stone-500" data-testid="assessment-loading">Loading…</p></Section>;

  const total = data.questions.length;
  const scaleLow = data.questions[0]?.options?.[0]?.value ?? 1;
  const scaleHigh = data.questions[0]?.options?.slice(-1)[0]?.value ?? 5;
  const current = data.questions[step];
  const answered = current ? answers[current.id] !== undefined : false;
  const anySafety = Object.values(safetyFlags).some(Boolean);

  const next = async () => {
    if (step < total - 1) { setStep((s) => s + 1); return; }
    setSubmitting(true);
    try {
      const res = await submitAssessment(id, answers);
      navigate(`/results/${res.submission_id}`);
    } catch (e) {
      toast.error("Could not calculate your result. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pb-8">
      {stage === "intro" && (
        <Section className="pt-14 sm:pt-20">
          <Link to="/assessments" className="text-sm text-stone-500 hover:text-[#1F3D2E]" data-testid="assessment-back-link">
            <i className="fa-solid fa-arrow-left text-xs mr-2" />All assessments
          </Link>
          <div className="max-w-2xl mt-6">
            <Eyebrow>{data.category} • {data.duration}{data.detailed ? "" : " • Preview"}</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-stone-900 leading-[1.08] mt-4">{data.title}</h1>
            <p className="text-lg text-stone-600 leading-relaxed mt-5">{data.intro}</p>

            <div className="mt-6 bg-white rounded-2xl border border-stone-200 p-6">
              <p className="text-sm text-stone-700"><i className="fa-solid fa-list-check text-[#1F3D2E] mr-2" />{total} questions, rated {scaleLow}–{scaleHigh}</p>
              <p className="text-sm text-stone-700 mt-2"><i className="fa-solid fa-lock text-[#1F3D2E] mr-2" />Your summary appears instantly and privately</p>
              <p className="text-sm text-stone-700 mt-2"><i className="fa-solid fa-envelope text-[#1F3D2E] mr-2" />Full breakdown by email is optional</p>
            </div>

            {data.safety?.length > 0 && (
              <div className="mt-6 bg-[#FBF3E9] rounded-2xl border border-[#C9A962]/50 p-6" data-testid="safety-screen">
                <p className="text-sm font-semibold text-stone-800 flex items-center gap-2"><i className="fa-solid fa-shield-heart text-[#8B6F3D]" /> A quick safety check</p>
                <p className="text-xs text-stone-600 mt-1">Please tick any that apply. Your safety matters more than any assessment.</p>
                <div className="mt-4 space-y-2.5">
                  {data.safety.map((s, i) => (
                    <label key={s} className="flex items-start gap-3 text-sm text-stone-700" data-testid={`safety-item-${i}`}>
                      <input type="checkbox" checked={!!safetyFlags[i]} onChange={(e) => setSafetyFlags((f) => ({ ...f, [i]: e.target.checked }))} className="mt-1 accent-[#8B6F3D] h-4 w-4" />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
                {anySafety && (
                  <div className="mt-4 text-sm text-stone-800 bg-white rounded-xl p-4 border border-[#C9A962]/40" data-testid="safety-warning">
                    {data.safety_note} If you're in immediate danger, contact your local emergency number now.
                  </div>
                )}
              </div>
            )}

            <button data-testid="begin-assessment-button" onClick={() => { setStage("quiz"); window.scrollTo({ top: 0 }); }} className="btn-terracotta rounded-full px-8 py-4 text-sm font-semibold mt-7">
              Begin assessment
            </button>
            <DisclaimerNote className="mt-6" text={data.disclaimer} />
          </div>
        </Section>
      )}

      {stage === "quiz" && (
        <Section className="pt-14 sm:pt-20 max-w-2xl">
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span data-testid="quiz-progress-label">Question {step + 1} of {total}</span>
            <span>{Math.round((step / total) * 100)}%</span>
          </div>
          <Progress value={(step / total) * 100} className="mt-3 h-2" data-testid="quiz-progress" />

          <AnimatePresence mode="wait">
            <motion.div key={current.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3, ease: "easeOut" }} className="mt-8">
              <h2 className="font-playfair text-2xl sm:text-3xl text-stone-900 leading-snug">{current.text}</h2>

              {current.low_anchor && (
                <div className="flex justify-between gap-4 text-xs text-stone-500 mt-4">
                  <span className="max-w-[45%]"><strong>1</strong> — {current.low_anchor}</span>
                  <span className="max-w-[45%] text-right"><strong>5</strong> — {current.high_anchor}</span>
                </div>
              )}

              <div className={`mt-5 ${current.low_anchor ? "grid grid-cols-5 gap-2" : "grid gap-3"}`} data-testid="quiz-options">
                {current.options.map((opt) => {
                  const selected = answers[current.id] === opt.value;
                  if (current.low_anchor) {
                    return (
                      <button key={opt.value} data-testid={`quiz-option-${opt.value}`} onClick={() => setAnswers((a) => ({ ...a, [current.id]: opt.value }))}
                        className={`rounded-xl border py-4 text-lg font-semibold transition-all ${selected ? "border-[#1F3D2E] bg-[#1F3D2E] text-white ring-2 ring-[#C9A962]/50" : "border-stone-300 bg-white text-stone-700 hover:border-[#1F3D2E]/50"}`}>
                        {opt.label}
                      </button>
                    );
                  }
                  return (
                    <button key={opt.value} data-testid={`quiz-option-${opt.value}`} onClick={() => setAnswers((a) => ({ ...a, [current.id]: opt.value }))}
                      className={`text-left rounded-xl border px-5 py-4 text-sm font-medium transition-all ${selected ? "border-[#1F3D2E] bg-[#1F3D2E]/8 text-stone-900 ring-2 ring-[#1F3D2E]/25" : "border-stone-300 bg-white text-stone-700 hover:border-[#1F3D2E]/50"}`}>
                      <span className={`inline-grid place-items-center h-5 w-5 rounded-full border mr-3 align-middle ${selected ? "bg-[#1F3D2E] border-[#1F3D2E]" : "border-stone-400"}`}>
                        {selected && <i className="fa-solid fa-check text-white text-[10px]" />}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            <button data-testid="quiz-back-button" onClick={() => (step === 0 ? setStage("intro") : setStep((s) => s - 1))} className="text-sm font-semibold text-stone-500 hover:text-stone-800">
              <i className="fa-solid fa-arrow-left text-xs mr-2" />Back
            </button>
            <button data-testid="quiz-next-button" onClick={next} disabled={!answered || submitting} className="btn-terracotta rounded-full px-8 py-3.5 text-sm font-semibold disabled:opacity-50">
              {step === total - 1 ? (submitting ? "Calculating…" : "See my results") : "Next"}
            </button>
          </div>
        </Section>
      )}
    </div>
  );
}

export function ResultView({ result, onRetake }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailed, setEmailed] = useState(false);
  const pct = result.percent;
  const scaleMax = result.scale_max || 5;

  const copyLink = async () => {
    const url = `${window.location.origin}/results/${result.submission_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Results link copied to your clipboard.");
    } catch {
      toast.error("Couldn't copy the link. Please copy it from your address bar.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return toast.error("Please enter your name and email.");
    if (!consent) return toast.error("Please check the consent box to receive your results.");
    setSending(true);
    try {
      const res = await requestFullResults({ submission_id: result.submission_id, name, email, consent });
      toast.success(res.message);
      setEmailed(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send results. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <Section className="pt-14 sm:pt-20 max-w-3xl">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="rounded-2xl bg-[#EEF2ED] border border-[#1F3D2E]/15 p-5 flex items-start gap-3 mb-8" data-testid="result-thankyou">
          <i className="fa-solid fa-circle-check text-[#4A7C59] text-xl mt-0.5" />
          <div>
            <p className="font-playfair text-lg text-stone-900">Thank you for completing this assessment</p>
            <p className="text-sm text-stone-600 mt-1">Your results are below. This is your private results page &mdash; bookmark or save the link to return to it anytime.</p>
            {result.submission_id && (
              <button
                type="button"
                onClick={copyLink}
                data-testid="result-copy-link"
                className="inline-flex items-center gap-2 mt-3 rounded-full border border-[#1F3D2E]/25 bg-white px-4 py-2 text-xs font-semibold text-[#1F3D2E] hover:bg-[#1F3D2E]/5 transition-colors"
              >
                <i className="fa-solid fa-link" /> Copy results link
              </button>
            )}
          </div>
        </div>
        <Eyebrow>Your immediate summary</Eyebrow>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-stone-900 mt-4">{result.assessment_title}</h1>

        {/* Score + band */}
        <div className="bg-white rounded-[2rem] border border-stone-200 p-8 mt-8" data-testid="result-summary-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="relative grid place-items-center h-28 w-28 shrink-0">
              <svg viewBox="0 0 36 36" className="h-28 w-28 -rotate-90">
                <path d="M18 2.5a15.5 15.5 0 1 1 0 31 15.5 15.5 0 0 1 0-31" fill="none" stroke="#EFEAE0" strokeWidth="3" />
                <path d="M18 2.5a15.5 15.5 0 1 1 0 31 15.5 15.5 0 0 1 0-31" fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${pct}, 100`} />
              </svg>
              <span className="absolute font-playfair text-2xl text-stone-900" data-testid="result-score">{pct}%</span>
            </div>
            <div>
              <p className="overline-label">{result.detailed ? "Your result" : "Preview result"}</p>
              <h2 className="font-playfair text-3xl text-stone-900 mt-1" data-testid="result-band-label">{result.band_label}</h2>
              <p className="text-stone-600 mt-2 leading-relaxed" data-testid="result-summary-text">{result.summary}</p>
            </div>
          </div>

          {/* Readiness (Sacred Flame) */}
          {result.readiness && (
            <div className="mt-8 bg-[#FBF3E9] rounded-xl p-5 border border-[#C9A962]/40" data-testid="result-readiness">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-stone-800">Readiness: {result.readiness.label}</p>
                <span className="font-playfair text-xl text-[#8B6F3D]">{result.readiness.percent}%</span>
              </div>
              <p className="text-sm text-stone-600 mt-1.5 leading-relaxed">{result.readiness.meaning}</p>
            </div>
          )}

          {/* Dimension breakdown */}
          {result.dimensions?.length > 0 && (
            <div className="mt-8" data-testid="result-dimensions">
              <p className="overline-label mb-3">Dimension breakdown</p>
              <div className="space-y-3">
                {result.dimensions.map((d) => (
                  <div key={d.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-stone-700">{d.name}</span>
                      <span className="text-stone-500">{d.average.toFixed(1)}/{scaleMax}</span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#1F3D2E]" style={{ width: `${d.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Focus / profile / pattern */}
          {result.focus && (
            <div className="mt-8 bg-[#EEF2ED] rounded-xl p-5 border border-[#1F3D2E]/15" data-testid="result-focus">
              <div className="flex items-center gap-3">
                {result.focus.start && (
                  <span className="bg-[#1F3D2E] text-white text-xs font-mono px-2.5 py-1 rounded-full tracking-wider">{result.focus.start}</span>
                )}
                <p className="font-playfair text-lg text-stone-900">{result.focus.title}</p>
              </div>
              <p className="text-sm text-stone-700 mt-2 leading-relaxed">{result.focus.meaning}</p>
              <p className="text-sm text-stone-700 mt-2 leading-relaxed">{result.focus.direction}</p>
              {result.focus.language && (
                <p className="text-sm text-stone-600 italic mt-3 border-l-2 border-[#C9A962] pl-3">Try: {result.focus.language}</p>
              )}
              {result.focus.growth_area && (
                <p className="text-xs text-stone-500 mt-3">Suggested first skill area: <strong>{result.focus.growth_area}</strong></p>
              )}
            </div>
          )}

          {/* Insights (lite) */}
          {result.insights?.length > 0 && (
            <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="result-insights">
              {result.insights.map((ins, i) => (
                <div key={ins} className="bg-[#F0EBE0] rounded-xl p-4">
                  <i className="fa-solid fa-feather text-[#8B6F3D] mb-2" />
                  <p className="text-sm text-stone-700 leading-relaxed">{ins}</p>
                </div>
              ))}
            </div>
          )}

          {/* Sacred Flame path */}
          {result.path && (
            <div className="mt-8" data-testid="result-path">
              <p className="overline-label mb-3">The Sacred Flame path</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {result.path.map((p) => (
                  <div key={p.step} className="bg-[#1F3D2E] rounded-xl p-4 text-center">
                    <p className="font-mono text-[#C9A962] text-xs tracking-wider">{p.step}</p>
                    <p className="text-xs text-stone-200 mt-1.5 leading-snug">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 bg-[#EEF2ED] rounded-xl p-5 border border-[#1F3D2E]/15">
            <p className="text-sm text-stone-800 leading-relaxed"><strong>A suggested next step:</strong> {result.recommendation}</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <Link to="/consultation" data-testid="result-consultation-cta" className="btn-terracotta rounded-full px-7 py-4 text-sm font-semibold text-center flex-1">
            Request a free 30-minute consultation
          </Link>
          <button onClick={onRetake} data-testid="result-retake" className="rounded-full px-7 py-4 text-sm font-semibold text-center border border-stone-300 text-stone-700 hover:bg-white">Retake</button>
        </div>

        <div className="mt-8 bg-white rounded-[2rem] border border-stone-200 p-8">
          {emailed ? (
            <div className="text-center py-4" data-testid="result-email-success">
              <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-[#EEF2ED] mb-3"><i className="fa-solid fa-check text-[#1F3D2E] text-xl" /></div>
              <h3 className="font-playfair text-xl text-stone-900">Your full results are on the way</h3>
              <p className="text-stone-600 text-sm mt-2">We've emailed a fuller breakdown and gentle next steps. Check your inbox in a few minutes.</p>
            </div>
          ) : (
            <>
              <h3 className="font-playfair text-2xl text-stone-900">Want the full breakdown by email?</h3>
              <p className="text-stone-600 text-sm mt-2">We'll send a fuller explanation and gentle next steps. Optional, and yours to keep.</p>
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="result-email-form">
                <input data-testid="result-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-xl border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/30" />
                <input data-testid="result-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="rounded-xl border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/30" />
                <label className="sm:col-span-2 flex items-start gap-3 text-sm text-stone-600">
                  <input type="checkbox" data-testid="result-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#1F3D2E] h-4 w-4" />
                  <span>I agree to receive my assessment results and occasional follow-up by email. I understand this is a supportive tool, not medical advice.</span>
                </label>
                <button type="submit" disabled={sending} data-testid="result-email-submit" className="btn-terracotta sm:col-span-2 rounded-full px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
                  {sending ? "Sending…" : "Email me my full results"}
                </button>
              </form>
            </>
          )}
        </div>

        <DisclaimerNote className="mt-6" text={result.disclaimer} />
      </motion.div>
    </Section>
  );
}
